import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Eye, EyeOff, Trash2, ImagePlus, Loader2, Camera, AlertTriangle } from 'lucide-react';
import {
  resolvePhotoUrls,
  addAnimalPhoto,
  setAnimalPhotoVisibility,
  removeAnimalPhoto,
  toast,
} from '@rehome/shared';

interface AnimalPhotoManagerProps {
  animalId: string;
  /** Publikus kép-URL-ek; a `gallery[0]` a borítókép. */
  gallery: string[];
  /** Privát képek tároló-útvonalai. */
  privateGallery: string[];
  /** Sikeres módosítás után az adatlap újratöltésére. */
  onChanged: () => void;
}

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/**
 * Az állat képeinek kezelése: hozzáadás, törlés, láthatóság.
 *
 * ── Miért azonnaliak a műveletek, és miért nem az űrlap mentésének részei ──
 * Egy kép láthatóságának váltása a tárolóban FIZIKAI mozgatás (privát és
 * publikus bucket között), nem egy mező átírása. Ha ez az űrlap mentésével
 * együtt futna, egy félbeszakadt mentés a tárolót és az adatbázist külön
 * állapotban hagyná. Így viszont minden művelet önmagában zárul le, és a
 * lista mindig a szerveren, a tényleges aktuális értékre módosul — nem egy
 * elavult kliensoldali másolatot ír vissza egy párhuzamosan dolgozó kolléga
 * módosítására.
 *
 * A láthatóság váltása szándékosan kétlépcsős (választás, majd `Mentés`):
 * egy véletlen kattintás nem teheti nyilvánossá a képet.
 */
export const AnimalPhotoManager: React.FC<AnimalPhotoManagerProps> = ({
  animalId, gallery, privateGallery, onChanged,
}) => {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [busyRef, setBusyRef] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  /** Melyik képnél van nyitva a láthatóság-választó, és mi a kiválasztott érték. */
  const [editing, setEditing] = useState<{ ref: string; makePublic: boolean } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const refs = [...gallery, ...privateGallery];
  const refsKey = refs.join('|');

  // A privát képekhez aláírt link kell — egyetlen kötegelt kérésben, nem
  // képenként (l. resolvePhotoUrls).
  useEffect(() => {
    let cancelled = false;
    const list = refsKey ? refsKey.split('|') : [];
    if (list.length === 0) { setUrls({}); return; }
    resolvePhotoUrls(list).then(resolved => { if (!cancelled) setUrls(resolved); });
    return () => { cancelled = true; };
  }, [refsKey]);

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    let added = 0;
    for (let i = 0; i < fileList.length; i++) {
      const dataUrl = await readAsDataUrl(fileList[i]);
      const ref = await addAnimalPhoto(animalId, dataUrl, refs.length + i);
      if (ref) added++;
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
    if (added > 0) {
      toast.success(added === 1 ? 'A kép feltöltve — egyelőre privát.' : `${added} kép feltöltve — egyelőre privát.`);
      onChanged();
    }
  };

  const applyVisibility = async () => {
    if (!editing) return;
    setBusyRef(editing.ref);
    const result = await setAnimalPhotoVisibility(animalId, editing.ref, editing.makePublic);
    setBusyRef(null);
    setEditing(null);
    if (result) onChanged();
  };

  const handleRemove = useCallback(async (ref: string) => {
    // A platformon és a listákban is kellene legalább egy kép — az utolsót
    // ezért csak akkor engedjük törölni, ha másikat töltenek fel helyette.
    if (refs.length <= 1) {
      toast.error('Az utolsó képet nem lehet törölni. Tölts fel előbb egy másikat.');
      return;
    }
    setBusyRef(ref);
    const ok = await removeAnimalPhoto(animalId, ref);
    setBusyRef(null);
    if (ok) { toast.success('A kép törölve.'); onChanged(); }
  }, [animalId, refs.length, onChanged]);

  const noPublicPhoto = gallery.length === 0 && privateGallery.length > 0;

  return (
    <div>
      {noPublicPhoto && (
        <div className="flex items-start gap-2 mb-3 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Egyetlen kép sem publikus, ezért az állat <strong>kép nélkül</strong> jelenik meg az
            örökbefogadói felületen. A képre kattintva teheted publikussá.
          </span>
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {refs.map((ref, i) => {
          const isPublic = i < gallery.length;
          const url = urls[ref];
          const isBusy = busyRef === ref;
          const isEditing = editing?.ref === ref;

          return (
            <div key={ref} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100 group">
              {url ? (
                <img src={url} alt={`Fotó ${i + 1}`} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <Camera className="w-6 h-6" />
                </div>
              )}

              {/* Borító jelölés csak publikus képnél van értelme: privát képet
                  a platform sosem mutatna meg borítóként sem. */}
              {isPublic && i === 0 && (
                <span className="absolute top-1 left-1 bg-teal-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                  Borítókép
                </span>
              )}

              <button
                type="button"
                onClick={() => setEditing(isEditing ? null : { ref, makePublic: !isPublic })}
                disabled={isBusy}
                className={`absolute bottom-1 left-1 flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded transition-colors ${
                  isPublic ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-800/80 text-white hover:bg-gray-900'
                }`}
                aria-label={`Láthatóság módosítása — jelenleg ${isPublic ? 'publikus' : 'privát'}`}
              >
                {isPublic ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                {isPublic ? 'Publikus' : 'Privát'}
              </button>

              <button
                type="button"
                onClick={() => handleRemove(ref)}
                disabled={isBusy}
                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-90 hover:bg-red-600 transition-colors"
                aria-label="Fotó törlése"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>

              {isBusy && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
                </div>
              )}

              {/* Kétlépcsős váltás: választás, majd Mentés. */}
              {isEditing && !isBusy && (
                <div className="absolute inset-0 bg-white/95 p-2 flex flex-col justify-center gap-1.5 text-[11px]">
                  {[
                    { value: false, label: 'Privát', hint: 'Csak a menhely látja' },
                    { value: true, label: 'Publikus', hint: 'Látszik a platformon' },
                  ].map(opt => (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => setEditing({ ref, makePublic: opt.value })}
                      className={`text-left px-2 py-1 rounded border transition-colors ${
                        editing.makePublic === opt.value
                          ? 'border-teal-500 bg-teal-50 text-teal-800 font-semibold'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {opt.label}
                      <span className="block text-[10px] font-normal text-gray-400">{opt.hint}</span>
                    </button>
                  ))}
                  <div className="flex gap-1 mt-0.5">
                    <button
                      type="button"
                      onClick={applyVisibility}
                      disabled={editing.makePublic === isPublic}
                      aria-label="Láthatóság mentése"
                      className="flex-1 bg-teal-600 text-white rounded px-2 py-1 font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-teal-700 transition-colors"
                    >
                      Mentés
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(null)}
                      className="px-2 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                    >
                      Mégse
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-teal-400 hover:text-teal-600 transition-colors disabled:opacity-50"
        >
          {uploading
            ? <Loader2 className="w-6 h-6 animate-spin" />
            : <><ImagePlus className="w-6 h-6" /><span className="text-[11px] font-medium">Új fotó</span></>}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        onChange={e => handleFiles(e.target.files)}
        className="hidden"
      />

      <p className="text-[11px] text-gray-400 mt-2">
        Minden feltöltött kép alapértelmezetten privát, és a hosszabbik éle
        automatikusan 720 képpontra méreteződik.
      </p>
    </div>
  );
};
